import {
    Button,
    Cascader,
    CascaderProps,
    ConfigProvider,
    Divider,
    Empty,
    InputRef,
    Modal,
    ModalFuncProps,
    Select,
    Space,
    Table,
    Tooltip,
    notification
} from "antd";
import SafeAvatar from "../../components/SafeAvatar";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getPipelinesService, getEnvironmentService } from "../../backend";
import { useParams, useSearchParams } from "react-router-dom";
import { usePipelines, useEnvironmentsForPipelines, pipelineKeys } from "../../hooks/queries/pipelines";
import { environmentKeys } from "../../hooks/queries/environments";
import GitCommitLink from "../../components/GitCommitLink";
import moment from "moment";
import BuildStatusTag from "../../components/BuildStatusTag";
import DeploymentStatusTag, { isDeploymentStatusInProgress } from "../../components/DeploymentStatusTag";
import { Link } from "react-router-dom";
import { 
    Clock, 
    Zap, 
    Package, 
    Rocket, 
    LayoutGrid, 
    ArrowRight, 
    GitBranch, 
    XCircle, 
    Filter, 
    Folder, 
    Loader2, 
    RefreshCw, 
    Ban,
    GitMerge
} from "lucide-react";
import { PipelineItem } from "../../types";
import BuildDetailsDrawer from "../../components/BuildDetailsDrawer";
import ComponentDeployDetailsDrawer from "../../components/ComponentDeployDetailsDrawer";
import { newUpdateNotificationsState } from "../../recoil/notification";
import { useRecoilValue } from "recoil";
import AppDeployDetailsDrawer from "../../components/AppDeployDetailsDrawer";
import { ColumnsType } from "antd/es/table";
import { trackCancelDeployment } from "../../backend/tracking/deployment";
import { Pipeline } from "@microtica/ms-ap-sdk";
import GitIcon from "../../components/GitIcon";
import CommitTooltip from "../../components/CommitTooltip";
import Search from "antd/es/input/Search";
import Fuse, { FuseResultMatch } from 'fuse.js'
import InfiniteScroll from "react-infinite-scroll-component";

interface Option {
    value?: string;
    label: React.ReactNode;
    children?: Option[];
    isLeaf?: boolean;
    filterMetadata?: Pipeline;
    matches?: FuseResultMatch[];
}

const ProjectPipelines = () => {
    const { projectId } = useParams() as { projectId: string; }
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const updates = useRecoilValue(newUpdateNotificationsState);
    const [selectedPipeline, setSelectedPipeline] = useState<PipelineItem>();
    const [showPipelineDetails, setShowPipelineDetails] = useState(false);
    const [highlightedPipelineId, setHighlightedPipelineId] = useState<string | null>(null);
    const [selectedEnvs, setSelectedEnvs] = useState<string[]>([]);
    const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
    const [reloading, setReloading] = useState(false);
    const pageSize = 15;
    
    // Fetch environments for filter dropdown
    const { data: envs = [] } = useEnvironmentsForPipelines(projectId);
    
    // Fetch pipelines with infinite query
    const {
        data: pipelinesData,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        refetch,
    } = usePipelines(
        projectId,
        { envIds: selectedEnvs, repoNames: selectedRepos },
        pageSize
    );
    
    // Flatten all pages into a single array
    const pipelines = useMemo(() => {
        return pipelinesData?.pages.flatMap(page => page.pipelines) ?? [];
    }, [pipelinesData]);
    
    // Extract repos from pipelines
    const repos = useMemo(() => {
        return [...new Set(pipelines.flatMap(item => item.targets.map(target => target.repositoryName)))]
            .filter(r => !!r);
    }, [pipelines]);
    
    // Total count from first page
    const total = pipelinesData?.pages[0]?.total ?? 0;
    
    const filteredPipelines = pipelines;
    const loading = isFetching && pipelines.length === 0;
    const loadingMore = isFetchingNextPage;
    const hasMore = hasNextPage ?? false;
    const [showFilters, setShowFilters] = useState(false);
    const [, setModal] = useState<{
        destroy: () => void;
        update: (configUpdate: ModalFuncProps) => void;
    }>();

    const [allPipelines, setAllPipelines] = useState<Pipeline[]>([]);
    const [allPipelinesLoading, setAllPipelinesLoading] = useState(false);
    const [triggerOptions, setTriggerOptions] = useState<Option[]>([]);
    const [filteredTriggerOptions, setFilteredTriggerOptions] = useState<Option[]>([]);
    const [triggerFilter, setTriggerFilter] = useState<string>();
    const [selectedPipelineBranch, setSelectedPipelineBranch] = useState<string[]>([]);

    const searchPipelinesRef = useRef<InputRef>(null);
    const processedParamsRef = useRef<string | null>(null);
    const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);


    const fuseOptions = {
        keys: [
            "filterMetadata.id",
            "filterMetadata.name",
            "filterMetadata.repositoryUrl",
            "filterMetadata.modifiedFiles",
            "children.value"
        ]
    };

    // Invalidate pipelines when Recoil notifications arrive
    useEffect(() => {
        if (updates.some(update => update.projectId === projectId)) {
            // Use listByProject to match all pipeline queries for this project regardless of filters
            queryClient.invalidateQueries({ queryKey: pipelineKeys.listByProject(projectId) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updates]);

    // Handle highlighting pipeline item from notification click
    useEffect(() => {
        const pipelineIdParam = searchParams.get('pipelineId');
        const buildIdParam = searchParams.get('buildId');
        const paramsKey = pipelineIdParam && buildIdParam ? `${pipelineIdParam}-${buildIdParam}` : null;
        
        // Skip if we've already processed these params
        if (paramsKey && paramsKey === processedParamsRef.current) {
            return;
        }
        
        // Clear any existing timeout if params changed
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = null;
        }
        
        if (pipelineIdParam && buildIdParam && filteredPipelines && filteredPipelines.length > 0) {
            // Find the matching pipeline item
            const matchingPipeline = filteredPipelines.find(item => 
                item.build?.pipelineId === pipelineIdParam && 
                item.build?.id === buildIdParam
            );
            
            if (matchingPipeline) {
                // Mark these params as processed
                processedParamsRef.current = paramsKey;
                
                // Highlight the matching pipeline
                setHighlightedPipelineId(matchingPipeline.id);
                
                // Remove query parameters from URL and highlight after 3 seconds
                highlightTimeoutRef.current = setTimeout(() => {
                    setHighlightedPipelineId(null);
                    // Remove query parameters from URL after highlight is cleared
                    // Read current params from window to ensure we have the latest
                    const currentParams = new URLSearchParams(window.location.search);
                    currentParams.delete('pipelineId');
                    currentParams.delete('buildId');
                    setSearchParams(currentParams, { replace: true });
                    // Clear the processed ref so we can process new params if needed
                    processedParamsRef.current = null;
                    highlightTimeoutRef.current = null;
                }, 2000);
            }
        } else if (!pipelineIdParam && !buildIdParam) {
            // Clear the ref when params are removed
            processedParamsRef.current = null;
        }
        
        // Cleanup: clear timeout if component unmounts or effect re-runs with different params
        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
                highlightTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredPipelines, searchParams]);

    useEffect(() => {
        if (!triggerFilter) {
            setFilteredTriggerOptions(triggerOptions)
        } else {
            const fuse = new Fuse(triggerOptions, fuseOptions);
            setFilteredTriggerOptions(
                fuse.search(triggerFilter).map(s => s.item)
            )
        }

    }, [triggerOptions, triggerFilter]);

    const reloadPipelines = async () => {
        setReloading(true);
        await refetch();
        setReloading(false);
    }


    const loadAllPipelines = async () => {
        setAllPipelinesLoading(true);


        const { data: { pipelines: allPipelines } } = await getPipelinesService().listPipelines(projectId);

        setAllPipelines(allPipelines);
        setTriggerOptions(allPipelines.map(p => ({
            value: p.id,
            label: <div style={{ padding: "10px" }}>
                <div>
                    <GitIcon provider={p.gitAccountType as any} /> {p.repositoryUrl?.split("/").reverse().slice(0, 2).reverse().join("/")}
                </div>
                <div className="gray-text">
                    {p.name}{p.modifiedFiles && <><Divider type="vertical" /><span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}><Folder size={14} /> {p.modifiedFiles}</span></>}
                </div>
            </div>,
            filterMetadata: p,
            isLeaf: false
        })));
        setAllPipelinesLoading(false);
        searchPipelinesRef.current?.focus({ cursor: 'all' });
    }

    const loadBranches = async (selectedOptions: Option[]) => {
        const targetOption = selectedOptions[selectedOptions.length - 1];
        const selectedPipeline = allPipelines.find(p => p.id === targetOption.value)

        if (selectedPipeline) {
            const { data: { branches } } = await getPipelinesService().listGitAccountRepositoryBranches(
                projectId,
                selectedPipeline.gitAccountId!,
                selectedPipeline.repositoryUrl!
            );

            setTriggerOptions(
                triggerOptions.reduce((acc, option) => {
                    if (option.value === selectedPipeline.id) {
                        option.children = branches.map(b => ({
                            value: b,
                            label: <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                <GitBranch size={14} /> {b}
                                            </span>
                        }))
                    }
                    acc.push(option);

                    return acc;
                }, [] as Option[])
            )
        }
    }

    const getPipelineTypeColumnContent = (item: PipelineItem) => {
        if (item.status === "QUEUED") {
            return {
                color: "#9ca3af",
                icon: <Clock size={16} />,
                label: "Queued",
                tooltip: "Pipeline is queued"
            };
        }
        if (item.type === "buildonly") {
            return {
                color: "#f59e0b",
                icon: <Zap size={16} />,
                label: "Build",
                tooltip: "No deployment steps are included in this pipeline, thus, no deployment will be initiated."
            };
        }
        if (item.type === "build") {
            return {
                color: "#f59e0b",
                icon: <Zap size={16} />,
                label: "Build",
                tooltip: "No matching criteria to initiate deployment."
            };
        }
        if (item.type === "template") {
            return {
                color: "#8b5cf6",
                icon: <Package size={16} />,
                label: "Template",
                tooltip: "A temporary pipeline orchestrating the deployment of a template."
            };
        }
        return {
            color: "#077BB5",
            icon: <Rocket size={16} />,
            label: "Deploy",
            tooltip: "This pipeline initiates deployment for components or apps."
        };
    }

    const PipelineTypeColumn = ({ item }: { item: PipelineItem }) => {
        const typeContent = getPipelineTypeColumnContent(item);
        const badge = (
            <div style={{
                flexShrink: 0,
                padding: "7px",
                borderRadius: "6px",
                backgroundColor: `${typeContent.color}15`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px"
            }}>
                <span style={{ 
                    color: typeContent.color,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    lineHeight: 1,
                    opacity: 0.75
                }}>
                    {typeContent.icon}
                </span>
            </div>
        );

        return (
            <div style={{ display: "flex", alignItems: "center" }}>
                {typeContent.tooltip ? (
                    <Tooltip title={typeContent.tooltip}>
                        {badge}
                    </Tooltip>
                ) : badge}
            </div>
        );
    }

    const EnvironmentLink = ({ envId }: { envId: string }) => {
        const [isHovered, setIsHovered] = useState(false);
        
        return (
            <Link 
                to={`/projects/${projectId}/environments/${envId}`} 
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{ 
                    fontWeight: 600, 
                    color: "#111827",
                    fontSize: "12px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px"
                }}
            >
                <LayoutGrid size={12} color="#6b7280" />
                {envId}
                {isHovered && (
                    <ArrowRight size={10} color="#6b7280" style={{ marginLeft: "2px" }} />
                )}
            </Link>
        );
    }

    const PipelineTargetColumn = ({ item }: { item: PipelineItem }) => {
        const [expanded, setExpanded] = useState(false);
        const envIds = [...new Set(item.targets.map(t => t.envId))].filter(envId => !!envId);
        const MAX_VISIBLE_TARGETS = 1;

        if (item.status === "QUEUED" || item.status === "DEPLOY_QUEUED") {
            return (
                <span style={{ 
                    color: "#9ca3af", 
                    fontSize: "12px",
                    fontStyle: "italic"
                }}>
                    Pending targets...
                </span>
            );
        }
        if (item.type === "deploy") {
            if (!item.targets.length) {
                return (
                    <span style={{ 
                        color: "#9ca3af", 
                        fontSize: "12px",
                        fontStyle: "italic"
                    }}>
                        No targets
                    </span>
                );
            }

            const visibleTargets = expanded ? item.targets : item.targets.slice(0, MAX_VISIBLE_TARGETS);
            const hasMore = item.targets.length > MAX_VISIBLE_TARGETS;

            return (
                <div style={{ fontSize: "12px" }}>
                    {envIds.length > 0 && (
                        <div style={{ marginBottom: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {envIds.map((envId, index) => (
                                envId && (
                                    <span key={envId} style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <EnvironmentLink envId={envId} />
                                        {index < envIds.length - 1 && (
                                            <span style={{ color: "#d1d5db", margin: "0 4px" }}>•</span>
                                        )}
                                    </span>
                                )
                            ))}
                        </div>
                    )}
                    <div>
                        {visibleTargets.map((target, index) => {
                            const isComponent = target.type === "component";
                            const isApp = target.type === "app";
                            
                            return (
                                <div 
                                    key={`${target.name}-${index}`} 
                                    style={{ 
                                        marginBottom: index < visibleTargets.length - 1 ? "6px" : 0,
                                        lineHeight: "1.5"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                        {(isComponent || isApp) && (
                                            <Tooltip title={isComponent ? "Component deployment" : "Application deployment"}>
                                                <span style={{
                                                    fontSize: "10px",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.03em",
                                                    color: isComponent ? "#2563eb" : "#ea580c",
                                                    backgroundColor: isComponent ? "#dbeafe" : "#fed7aa",
                                                    padding: "2px 6px",
                                                    borderRadius: "3px",
                                                    lineHeight: 1,
                                                    cursor: "help"
                                                }}>
                                                    {isComponent ? "Comp" : "App"}
                                                </span>
                                            </Tooltip>
                                        )}
                                        <span style={{ 
                                            fontWeight: 500, 
                                            color: "#111827",
                                            fontSize: "12px"
                                        }}>
                                            {target.name}
                                        </span>
                                        {target.undeployed ? (
                                            <span style={{ 
                                                color: "#f59e0b", 
                                                fontSize: "12px",
                                                fontWeight: 400
                                            }}>
                                                {target.status.endsWith("_IN_PROGRESS") ? "(undeploying)" : "(undeployed)"}
                                            </span>
                                        ) : target.commit.sha || target.commit.version ? (
                                            <CommitTooltip 
                                                commit={{
                                                    message: target.commit.message,
                                                    sha: target.commit.sha,
                                                    version: target.commit.version,
                                                    branch: target.commit.name,
                                                    date: target.commit.date,
                                                    repository: target.repository,
                                                    repositoryName: target.repositoryName,
                                                    user: target.commit.user
                                                }}
                                                deploymentDate={item.startDate}
                                            >
                                                <span style={{ 
                                                    color: "#097bb5", 
                                                    fontSize: "12px"
                                                }}>
                                                    <GitCommitLink
                                                        hideIcon
                                                        version={target.commit.sha || target.commit.version}
                                                        repoUrl={target.repository}
                                                        branch={target.commit.name}
                                                    />
                                                </span>
                                            </CommitTooltip>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                        {hasMore && (
                            <Button
                                type="link"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpanded(!expanded);
                                }}
                                style={{ 
                                    padding: 0,
                                    height: "auto",
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    color: "#6b7280",
                                    marginTop: "4px"
                                }}
                            >
                                {expanded ? "Show less" : `+${item.targets.length - MAX_VISIBLE_TARGETS} more`}
                            </Button>
                        )}
                    </div>
                </div>
            );
        }
        if ((item.type === "buildonly" || item.type === "build") && item.status !== "QUEUED") {
            const target = item.targets[0];
            const buildMetadata = item.build?.metadata;
            const commit = buildMetadata?.commit || target?.commit;
            const repository = buildMetadata?.repository || target?.repository;
            const hasCommitInfo = commit && (commit.sha || commit.version);
            
            return (
                <div style={{ fontSize: "12px" }}>
                    <div style={{ 
                        color: "#6b7280", 
                        marginBottom: "4px",
                        fontSize: "11px",
                        fontWeight: 500
                    }}>
                        Build only
                    </div>
                    {hasCommitInfo ? (
                        <CommitTooltip 
                            commit={{
                                message: commit.message,
                                sha: commit.sha,
                                version: commit.version,
                                branch: commit.name,
                                date: commit.date,
                                repository: repository,
                                repositoryName: target?.repositoryName,
                                user: commit.user
                            }}
                            deploymentDate={item.startDate}
                        >
                            <span style={{ fontSize: "12px" }}>
                                <GitIcon repoUrl={repository} />{" "}
                                <span style={{ color: "#097bb5" }}>
                                    <GitCommitLink
                                        hideIcon
                                        version={commit.sha || commit.version}
                                        repoUrl={repository}
                                        branch={commit.name}
                                    />
                                </span>
                            </span>
                        </CommitTooltip>
                    ) : (
                        <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                            No commit info available
                        </span>
                    )}
                </div>
            );
        }
        if (item.type === "template") {
            return (
                <div style={{ fontSize: "12px" }}>
                    <div style={{ marginBottom: "6px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {envIds.map((envId, index) => (
                            envId && (
                                <span key={envId} style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                    <EnvironmentLink envId={envId} />
                                    {index < envIds.length - 1 && (
                                        <span style={{ color: "#d1d5db", margin: "0 4px" }}>•</span>
                                    )}
                                </span>
                            )
                        ))}
                    </div>
                    <div style={{ 
                        color: "#6b7280", 
                        fontSize: "11px",
                        fontWeight: 400
                    }}>
                        Template deployment
                    </div>
                </div>
            );
        }
        return <></>;
    }

    const PipelineTimeColumn = ({ item }: { item: PipelineItem }) => {
        // Remaining days until next Monday, because we cleanup template pipelines each Monday at 1:00 AM
        const millisecondsUntilNextMonday = (8 - new Date().getDay()) % 7 * 24 * 60 * 60 * 1000;
        const expiration = Math.floor(millisecondsUntilNextMonday / (1000 * 60 * 60));
        const expirationLabel = expiration <= 0 ? "Expires in less then an hour" : `Expires in ${expiration} hours`;
        const [timeValue, setTimeValue] = useState(item.type === "template" ? expirationLabel : moment(item.startDate).fromNow());

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            // deployment status should be DEPLOY_QUEUED or it should end with IN_PROGRESS
            // build status should be QUEUED or RUNNING
            const isRunningStatus = ["DEPLOY_QUEUED", "QUEUED", "RUNNING"].includes(item.status) || isDeploymentStatusInProgress(item.status);
            if (isRunningStatus) {
                intervalId = setInterval(() => {
                    setTimeValue(moment(item.startDate).fromNow());
                    // the interval should be cleared after deployed status, but we don't clear it here because: 
                    // deployed status will be received via notification which will cause rerender of this component, 
                    // the rerender will trigger unmount and the existing interval will be cleared and then isRunningStatus will become false
                }, 15 * 1000);
            }
            // Clean up interval on component unmount
            return () => clearInterval(intervalId);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [item.status]);

        return (
            <div style={{ fontSize: "12px", lineHeight: "1.5" }}>
                <div style={{ 
                    marginBottom: "6px", 
                    color: "#111827",
                    fontWeight: 500,
                    fontSize: "12px"
                }}>
                    {timeValue}
                </div>
                <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "6px", 
                    color: "#6b7280", 
                    fontSize: "11px"
                }}>
                    <SafeAvatar className="no-border" src={item.initiatorAvatar} size={16} />
                    <span style={{ fontWeight: 400 }}>{item.initiatorName}</span>
                </div>
            </div>
        )
    }

    const PipelineStatusColumn = ({ item }: { item: PipelineItem }) => {
        const statusTag = item.stage === "deploy" 
            ? <DeploymentStatusTag status={item.status} />
            : <BuildStatusTag status={item.status} statusCode={item.statusCode} />;

        return statusTag;
    }

    const PipelineActionsColumn = ({ item }: { item: PipelineItem }) => {
        const isCancellable = 
            item.status === "DEPLOY_QUEUED" ||
            (["CREATE_IN_PROGRESS", "UPDATE_IN_PROGRESS"].includes(item.status) && item.cloudProvider === "aws" && item.stage === "deploy") ||
            (item.status === "RUNNING" && item.stage === "build" && item.type !== "template");
        
        const tooltipText = isCancellable 
            ? (item.stage === "deploy" ? "Cancel Deployment" : "Cancel Build")
            : undefined;

        const handleCancelClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!isCancellable) return;
            
            if (item.status === "RUNNING" && item.stage === "build" && item.type !== "template") {
                showCancelBuildConfirm(item);
            } else {
                showCancelStageDeploymentConfirm(item);
            }
        };

        if (!isCancellable) {
            return null;
        }

        return (
            <Tooltip title={tooltipText}>
                <Button
                    size="small"
                    danger
                    icon={<Ban size={12} />}
                    onClick={handleCancelClick}
                    style={{ fontSize: "12px", height: "24px", padding: "0 8px" }}
                >
                    Cancel
                </Button>
            </Tooltip>
        );
    }


    const showCancelStageDeploymentConfirm = (pipeline: PipelineItem) => {
        const modal = Modal.confirm({
            title: "Cancel deployment",
            icon: <XCircle size={20} color="#ff4d4f" fill="#ff4d4f" />,
            content: "Are you sure you want to cancel the deployment? This action cannot be undone.",
            okText: "Cancel Deployment",
            cancelText: "No",
            centered: true,
            okButtonProps: {
                danger: true
            },
            async onOk() {
                return handleCancelDeployment(pipeline);
            },
            onCancel() {
                setModal(undefined);
            }
        });
        setModal(modal);
    }

    const showCancelBuildConfirm = (pipeline: PipelineItem) => {
        const modal = Modal.confirm({
            title: "Cancel build",
            icon: <XCircle size={20} color="#ff4d4f" fill="#ff4d4f" />,
            content: "Are you sure you want to cancel the build execution? This action cannot be undone.",
            okText: "Cancel Build",
            cancelText: "No",
            centered: true,
            okButtonProps: {
                danger: true
            },
            async onOk() {
                return handleCancelBuild(pipeline);
            },
            onCancel() {
                setModal(undefined);
            }
        });
        setModal(modal);
    }

    const handleCancelBuild = async (pipeline: PipelineItem) => {
        try {
            await getPipelinesService().stopPipelineExecution(
                projectId,
                pipeline.build?.pipelineId!,
                pipeline.build?.id!
            );
            // Invalidate pipelines query to reflect cancellation
            queryClient.invalidateQueries({ queryKey: pipelineKeys.listByProject(projectId) });
            notification.info({
                message: "Build canceled",
                description: "The pipeline build has been canceled"
            });
            trackCancelDeployment(pipeline.envId!);
        } catch (error: any) {
            notification.info({
                message: "Build canceled error",
                description: error.response?.data?.message || "An unknown erorr has occured"
            });
        }
        setModal(undefined);
    }

    const handleCancelDeployment = async (pipeline: PipelineItem) => {
        try {
            await getEnvironmentService().cancelDeploy(
                pipeline.envId!,
                projectId,
                pipeline.status === "DEPLOY_QUEUED" ? {
                    deploymentId: pipeline.id
                } : undefined
            );
            // Invalidate both pipelines and environment queries
            queryClient.invalidateQueries({ queryKey: pipelineKeys.listByProject(projectId) });
            if (pipeline.envId) {
                queryClient.invalidateQueries({ queryKey: environmentKeys.detail(projectId, pipeline.envId) });
            }
            notification.info({
                message: "Deployment canceled",
                description: "The environment deployment has been canceled"
            });
            trackCancelDeployment(pipeline.envId!);
        } catch (error: any) {
            notification.info({
                message: "Deployment canceled error",
                description: error.response?.data?.message || "An unknown erorr has occured"
            });
        }
        setModal(undefined);
    }

    // we want to update the columns only when the datasource (filteredPipelines) is updated
    // otherwise we will have unnecessary rerenders
    const columnsMemo = useMemo<ColumnsType<PipelineItem>>(() => [
        {
            title: 'Type',
            key: 'type',
            width: "100px",
            render: (item: PipelineItem) => <PipelineTypeColumn item={item} />
        },
        {
            title: 'Status',
            key: 'status',
            width: "200px",
            render: (item: PipelineItem) => <PipelineStatusColumn item={item} />
        },
        {
            title: 'Deployment Target',
            key: 'target',
            render: (item: PipelineItem) => <PipelineTargetColumn item={item} />
        },
        {
            title: 'Started',
            key: 'time',
            width: "20%",
            render: (item: PipelineItem) => <PipelineTimeColumn item={item} />
        },
        {
            title: '',
            key: 'actions',
            width: "120px",
            align: "right",
            render: (item: PipelineItem) => <PipelineActionsColumn item={item} />
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [filteredPipelines]);

    const handleSelectBranch: CascaderProps<Option>['onChange'] = async (value) => {
        try {
            await getPipelinesService().triggerPipeline(
                projectId,
                value[0],
                { ref: `refs/heads/${value[1]}` }
            );
            // Invalidate pipelines query to show the new pipeline run
            queryClient.invalidateQueries({ queryKey: pipelineKeys.listByProject(projectId) });
            notification.success({
                message: "Pipeline build triggered",
                description: "It could take a few moments for the changes to take effect.",
                icon: <Loader2 size={16} color="var(--primary-color)" style={{ animation: 'spin 1s linear infinite' }} />
            });
        } catch (error) {
            notification.error({
                message: "Error triggering build",
                description: (error as any).response?.data?.message || "We encountered an error while trying to trigger a build. Please try again."
            });
        } finally {
            setSelectedPipelineBranch([]);
        }
    }


    const activeFiltersCount = selectedEnvs.length + selectedRepos.length;

    return (
        <>
            <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Custom Header - outside of Table to avoid loading opacity */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: "#ffffff",
                    borderBottom: "1px solid #f0f0f0",
                    flexShrink: 0,
                    padding: "32px 50px 24px 50px"
                }}>
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0",
                        background: "transparent",
                        gap: "32px"
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
                                <div style={{ 
                                    width: "48px", 
                                    height: "48px", 
                                    borderRadius: "12px", 
                                    background: "linear-gradient(135deg, #077BB5 0%, #0a5a82 100%)", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "center",
                                    boxShadow: "0 4px 12px rgba(7, 123, 181, 0.2)"
                                }}>
                                    <GitMerge size={24} color="#fff" />
                                </div>
                                <div style={{ display: "flex", flexDirection: "column" }}>
                                    <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 4px 0", letterSpacing: "-0.02em" }}>Pipelines</span>
                                    <span style={{ fontSize: "14px", color: "#6B7280", margin: 0 }}>{total} {total === 1 ? 'run' : 'runs'}</span>
                                </div>
                            </div>

                            <Divider type="vertical" style={{ height: "32px", margin: "0 4px" }} />

                            <Button 
                                type={showFilters || activeFiltersCount > 0 ? "default" : "text"}
                                icon={<Filter size={14} />} 
                                onClick={() => setShowFilters(!showFilters)}
                                style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: "6px",
                                    color: activeFiltersCount > 0 ? "var(--primary-color)" : "#64748b",
                                    borderColor: activeFiltersCount > 0 ? "var(--primary-color)" : undefined,
                                    fontWeight: activeFiltersCount > 0 ? 600 : 400
                                }}
                            >
                                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                            </Button>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Cascader: {
                                            dropdownHeight: 500
                                        },
                                    },
                                }}
                            >
                                <Cascader
                                    options={filteredTriggerOptions}
                                    loadData={loadBranches}
                                    onChange={handleSelectBranch}
                                    value={selectedPipelineBranch}
                                    dropdownRender={(menus: ReactNode) => (
                                        <div style={{ minWidth: 320 }}>
                                            <Search
                                                className="full-width"
                                                style={{ padding: "10px" }}
                                                placeholder="Search pipelines..."
                                                onSearch={setTriggerFilter}
                                                defaultValue={triggerFilter}
                                                ref={searchPipelinesRef}
                                            />
                                            <Divider style={{ margin: 0 }} />
                                            {
                                                allPipelinesLoading ?
                                                    <Space className="flex-justify-center gray-text" style={{ padding: "10px" }}>
                                                        <Loader2 size={14} className="animate-spin" /> Loading pipelines...
                                                    </Space>
                                                    :
                                                    menus
                                            }
                                        </div>
                                    )}
                                >
                                    <Button 
                                        onClick={loadAllPipelines}
                                        icon={<Zap size={14} />}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            fontWeight: 500
                                        }}
                                    >
                                        Trigger Pipeline
                                    </Button>
                                </Cascader>
                            </ConfigProvider>

                            <Tooltip title="Refresh results">
                                <Button 
                                    icon={<RefreshCw size={14} />} 
                                    onClick={reloadPipelines}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    
                    {showFilters && (
                        <div style={{ 
                            padding: "24px 0 0 0",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            background: "transparent"
                        }}>
                            <Select
                                mode="tags"
                                placeholder="All Environments"
                                variant="filled"
                                style={{ flex: 1, maxWidth: "400px" }}
                                onChange={(value) => setSelectedEnvs(value)}
                                allowClear
                                maxTagCount="responsive"
                                value={selectedEnvs}
                                options={envs.map(env => ({ value: env.id, label: env.name }))}
                            />
                            <Select
                                mode="tags"
                                placeholder="All Repositories"
                                variant="filled"
                                style={{ flex: 1, maxWidth: "400px" }}
                                onChange={(value) => setSelectedRepos(value)}
                                allowClear
                                maxTagCount="responsive"
                                value={selectedRepos}
                                options={repos.map(repo => ({ value: repo, label: repo }))}
                            />
                        </div>
                    )}
                </div>

                {/* Table with infinite scroll */}
                <div style={{ position: 'relative', flex: 1, height: `calc(100vh - 56px - ${showFilters ? '200px' : '160px'})` }}>
                    {/* Fade gradient at the bottom - only when there's more to load */}
                    {filteredPipelines && filteredPipelines.length > 0 && hasMore && !loadingMore && (
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '80px',
                            background: 'linear-gradient(to top, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 40%, rgba(255, 255, 255, 0) 100%)',
                            pointerEvents: 'none',
                            zIndex: 10
                        }} />
                    )}
                    <div
                        id="pipelines-scrollable-container"
                        style={{
                            flex: 1,
                            overflow: 'auto',
                            height: '100%'
                        }}
                    >
                        <InfiniteScroll
                        dataLength={filteredPipelines?.length || 0}
                        next={() => fetchNextPage()}
                        hasMore={hasMore}
                        loader={
                            loadingMore ? (
                                <div style={{ 
                                    padding: "28px 20px", 
                                    textAlign: "center",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "12px"
                                }}>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "10px"
                                    }}>
                                        <div style={{
                                            width: "18px",
                                            height: "18px",
                                            border: "2.5px solid #f3f4f6",
                                            borderTop: "2.5px solid #077BB5",
                                            borderRadius: "50%",
                                            animation: "spin 0.75s linear infinite"
                                        }} />
                                        <span style={{ 
                                            color: "#4b5563", 
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            letterSpacing: "-0.01em"
                                        }}>
                                            Loading more...
                                        </span>
                                    </div>
                                </div>
                            ) : null
                        }
                        scrollableTarget="pipelines-scrollable-container"
                        endMessage={
                            filteredPipelines && filteredPipelines.length > 0 && !hasMore ? (
                                <div style={{ 
                                    padding: "28px 20px", 
                                    textAlign: "center",
                                    color: "#9ca3af",
                                    fontSize: "13px",
                                    fontWeight: 400
                                }}>
                                    <div style={{ marginBottom: "4px" }}>
                                        All pipelines loaded
                                    </div>
                                    <div style={{ fontSize: "12px", opacity: 0.7 }}>
                                        Showing {filteredPipelines.length} of {total} pipelines
                                    </div>
                                </div>
                            ) : null
                        }
                    >
                        <Table
                            className={`ant-table-selectable pipelines-table-full-height ${reloading ? 'pipelines-table-reloading' : ''} no-border`}
                            loading={loading}
                            dataSource={filteredPipelines}
                            pagination={false}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}
                            locale={{
                                emptyText:
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <>
                                                <div>
                                                    There are no pipelines available for the chosen period.
                                                </div>
                                                <div>
                                                    This could indicate a period of inactivity or no deployments during this time.
                                                </div>
                                            </>
                                        }
                                    />
                            }}
                            columns={columnsMemo}
                            onRow={item => {
                                const isHighlighted = highlightedPipelineId === item.id;
                                return {
                                    className: isHighlighted ? 'pipeline-row-highlighted' : '',
                                    onClick: () => {
                                        setHighlightedPipelineId(null); // Clear highlight on click
                                        setSelectedPipeline(item)
                                        setShowPipelineDetails(true);
                                    }, // click row
                                };
                            }}
                        />
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
            {
                showPipelineDetails ? (
                    <>
                        {
                            selectedPipeline?.build &&
                            <BuildDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                pipelineId={selectedPipeline.build?.pipelineId!}
                                buildId={selectedPipeline.build?.id!}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                        {
                            !selectedPipeline?.build && selectedPipeline?.targetType === "environment" &&
                            <ComponentDeployDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                envId={selectedPipeline.envId!}
                                cloudProvider={envs.find(e => e.id === selectedPipeline.envId!)?.cloudProvider!}
                                iac={envs.find(e => e.id === selectedPipeline.envId!)?.infrastructureAsCodeTool!}
                                deploymentId={selectedPipeline.id}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                        {
                            !selectedPipeline?.build && selectedPipeline?.targetType === "app" &&
                            <AppDeployDetailsDrawer
                                type={selectedPipeline.type}
                                projectId={projectId}
                                envId={selectedPipeline.envId!}
                                deploymentId={selectedPipeline.id}
                                open={showPipelineDetails}
                                onClose={() => setShowPipelineDetails(false)}
                            />
                        }
                    </>
                ) : null
            }
        </>
    )
}

export default ProjectPipelines;